import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X, ChevronDown } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { cities } from "@/data/cities";
import { services } from "@/data/services";
import { localize } from "@/utils/paths";

export const Navbar = ({ t, lang, setLang, scrollTo }) => {
  const [open, setOpen] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const [locOpen, setLocOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const homePath = localize("/", lang);
  const isHome = location.pathname === "/" || location.pathname === "/en";

  const goAnchor = (e, hash) => {
    e.preventDefault();
    setOpen(false);
    if (!isHome) {
      navigate(homePath);
      setTimeout(() => scrollTo(hash), 200);
    } else {
      scrollTo(hash);
    }
  };

  const Dropdown = ({ label, items, openState, setOpenState, testid }) => (
    <div className="relative" onMouseEnter={() => setOpenState(true)} onMouseLeave={() => setOpenState(false)}>
      <button
        data-testid={testid}
        className="flex items-center gap-1.5 text-sm tracking-wide text-smoke hover:text-ink transition-colors duration-300"
      >
        {label}
        <ChevronDown size={14} strokeWidth={1.5} className={`transition-transform duration-300 ${openState ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {openState && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 pt-3"
            data-testid={`${testid}-menu`}
          >
            <div className="bg-cream border border-line min-w-[220px]">
              {items.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={item.testid}
                  onClick={() => setOpenState(false)}
                  className="block px-5 py-3 text-sm text-smoke hover:bg-sand hover:text-ink transition-colors duration-200"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  const serviceItems = [
    ...services
      .map((s) => ({
        to: localize(`/leistungen/${s.slug}`, lang),
        label: s[lang].short,
        testid: `nav-service-${s.slug}`,
      })),
    {
      to: lang === "de" ? "/privater-chauffeur-schweiz" : "/en/private-chauffeur-switzerland",
      label: lang === "de" ? "Privater Chauffeur" : "Private Chauffeur",
      testid: "nav-service-chauffeur",
    },
  ];

  const cityItems = cities.map((c) => ({
    to: localize(`/standorte/${c.slug}`, lang),
    label: c.name,
    testid: `nav-location-${c.slug}`,
  }));

  const journalPath = lang === "en" ? "/en/journal" : "/ratgeber";

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-cream/85 border-b border-ink/10">
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-10 h-[72px]">
        <Link
          to={homePath}
          data-testid="nav-logo"
          onClick={() => setOpen(false)}
          className="font-heading text-xl md:text-2xl font-medium tracking-tight shrink-0 leading-none"
        >
          Swiss <span className="italic text-taupe">PA Services</span>
        </Link>

        <div className="hidden xl:flex items-center gap-7">
          <Link
            to={lang === "de" ? "/private-family-office-schweiz" : "/en/private-family-office-switzerland"}
            data-testid="nav-link-family-office"
            className="text-sm tracking-wide text-smoke hover:text-ink transition-colors duration-300 whitespace-nowrap"
          >
            {t.nav.familyOffice}
          </Link>
          <Dropdown label={t.nav.services} items={serviceItems} openState={svcOpen} setOpenState={setSvcOpen} testid="nav-services-dropdown" />
          <Dropdown label={t.nav.locations} items={cityItems} openState={locOpen} setOpenState={setLocOpen} testid="nav-locations-dropdown" />
          <Link
            to={journalPath}
            data-testid="nav-link-journal"
            className="text-sm tracking-wide text-smoke hover:text-ink transition-colors duration-300 whitespace-nowrap"
          >
            {t.nav.journal}
          </Link>
          <a
            href={homePath + "#kontakt"}
            data-testid="nav-link-contact"
            onClick={(e) => goAnchor(e, "#kontakt")}
            className="text-sm tracking-wide text-smoke hover:text-ink transition-colors duration-300"
          >
            {t.nav.contact}
          </a>
        </div>

        <div className="flex items-center gap-3 md:gap-5">
          <div className="flex items-center border border-line text-xs tracking-[0.15em] font-semibold" data-testid="language-switcher">
            <button
              data-testid="lang-de-button"
              onClick={() => setLang("de")}
              className={`px-3 py-2 transition-colors duration-300 ${lang === "de" ? "bg-ink text-cream" : "text-smoke hover:text-ink"}`}
            >
              DE
            </button>
            <button
              data-testid="lang-en-button"
              onClick={() => setLang("en")}
              className={`px-3 py-2 transition-colors duration-300 ${lang === "en" ? "bg-ink text-cream" : "text-smoke hover:text-ink"}`}
            >
              EN
            </button>
          </div>

          <a
            href={homePath + "#kontakt"}
            data-testid="nav-cta-button"
            onClick={(e) => goAnchor(e, "#kontakt")}
            className="hidden md:inline-flex items-center gap-2 bg-ink text-cream text-sm px-5 py-2.5 hover:bg-taupe transition-colors duration-300 whitespace-nowrap"
          >
            {t.nav.cta}
            <ArrowUpRight size={15} strokeWidth={1.5} />
          </a>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="xl:hidden p-2 text-ink"
            aria-label="Menu"
          >
            {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="xl:hidden overflow-hidden bg-cream border-b border-ink/10 max-h-[80vh] overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              <Link
                to={lang === "de" ? "/private-family-office-schweiz" : "/en/private-family-office-switzerland"}
                data-testid="mobile-nav-link-family-office"
                onClick={() => setOpen(false)}
                className="font-heading text-2xl font-light"
              >
                {t.nav.familyOffice}
              </Link>
              <div>
                <p className="text-xs uppercase tracking-[0.25em] font-bold text-taupe mb-4">{t.nav.services}</p>
                <div className="flex flex-col gap-4">
                  {serviceItems.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      data-testid={`mobile-${l.testid}`}
                      onClick={() => setOpen(false)}
                      className="font-heading text-2xl font-light"
                    >
                      {l.label}
                    </Link>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.25em] font-bold text-taupe mb-4">{t.nav.locations}</p>
                <div className="flex flex-wrap gap-2">
                  {cityItems.map((c) => (
                    <Link
                      key={c.to}
                      to={c.to}
                      data-testid={`mobile-${c.testid}`}
                      onClick={() => setOpen(false)}
                      className="border border-line px-4 py-2 text-sm"
                    >
                      {c.label}
                    </Link>
                  ))}
                </div>
              </div>
              <Link
                to={journalPath}
                data-testid="mobile-nav-link-journal"
                onClick={() => setOpen(false)}
                className="font-heading text-2xl font-light"
              >
                {t.nav.journal}
              </Link>
              <a
                href={homePath + "#kontakt"}
                data-testid="mobile-nav-cta-button"
                onClick={(e) => goAnchor(e, "#kontakt")}
                className="inline-flex items-center gap-2 bg-ink text-cream text-sm px-5 py-3 w-fit"
              >
                {t.nav.cta}
                <ArrowUpRight size={15} strokeWidth={1.5} />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
